import React from "react";
import {
    Box,
    Card,
    Grid,
    Typography,
    Chip
} from "@mui/material";

// React Simple Maps
import {
    ComposableMap,
    Geographies,
    Geography
} from "react-simple-maps";

// Recharts
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
    LabelList,
    LineChart,
    Line,
    CartesianGrid,
    Legend,
    PieChart,
    Pie
} from "recharts";


/* -----------------------------------------
DEFAULT DATA (YOUR DATA)
------------------------------------------ */

const defaultGeoUrl =
    "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const defaultUsaData = {
    TX: 80,
    CA: 65,
    NY: 55,
    FL: 45,
    WA: 35,
    GA: 50,
};

const defaultStateCodes = {
    1: "AL", 2: "AK", 4: "AZ", 5: "AR", 6: "CA",
    8: "CO", 9: "CT", 10: "DE", 12: "FL", 13: "GA",
    16: "ID", 17: "IL", 18: "IN", 19: "IA", 20: "KS",
    21: "KY", 22: "LA", 23: "ME", 24: "MD", 25: "MA",
    26: "MI", 27: "MN", 28: "MS", 29: "MO", 30: "MT",
    31: "NE", 32: "NV", 33: "NH", 34: "NJ", 35: "NM",
    36: "NY", 37: "NC", 38: "ND", 39: "OH", 40: "OK",
    41: "OR", 42: "PA", 44: "RI", 45: "SC", 46: "SD",
    47: "TN", 48: "TX", 49: "UT", 50: "VT", 51: "VA",
    53: "WA", 54: "WV", 55: "WI", 56: "WY"
};

const defaultGetColor = (value) => {
    if (value >= 75) return "rgba(76, 175, 80, 1)";
    if (value >= 50) return "rgba(76, 175, 80, 0.6)";
    if (value >= 25) return "rgba(76, 175, 80, 0.25)";
    return "#BDBDBD";
};

const defaultLegendColors = [
    "rgba(76, 175, 80, 1)",
    "rgba(76, 175, 80, 0.6)",
    "rgba(76, 175, 80, 0.25)",
    "#BDBDBD",
];

/* -----------------------------------------
   SAMPLE CHART DATA (OPTIONAL)
------------------------------------------ */
const defaultBarData = [
    { name: "RED BULL", value: 4.7, color: "#000000" },
    { name: "MONSTER", value: 3.4, color: "#333333" },
    { name: "CELSIUS", value: 1.3, color: "#555555" },
    { name: "ALANI", value: 0.8, color: "#777777" },
    { name: "GHOST", value: 0.4, color: "#AAAAAA" },
    { name: "C4", value: 0.4, color: "#f7e600" },
    { name: "ROCKSTAR", value: 0.3, color: "#DDDDDD" },
    { name: "NOS", value: 0.3, color: "#BCBCBC" },
    { name: "FUEL", value: 0.2, color: "#dcdcdc" },
    { name: "BLOOM", value: 0.1, color: "#e2d7ff" },
];

const defaultLineData = [
    { name: "Jan. 2025", a: 501, b: 752, c: 92 },
    { name: "Feb. 2025", a: 492, b: 806, c: 144 },
    { name: "Mar. 2025", a: 689, b: 1017, c: 88 },
    { name: "Apr. 2025", a: 528, b: 1005, c: 223 },
    { name: "May. 2025", a: 741, b: 1114, c: 172 },
    { name: "Jun. 2025", a: 755, b: 1002, c: 184 },
    { name: "Jul. 2025", a: 651, b: 1094, c: 169 },
    { name: "Aug. 2025", a: 654, b: 1167, c: 251 },
    { name: "Sep. 2025", a: 749, b: 996, c: 168 },
    { name: "Oct. 2025", a: 651, b: 1152, c: 248 },
    { name: "Nov. 2025", a: 762, b: 1052, c: 164 },
    { name: "Dec. 2025", a: 841, b: 1247, c: 148 },
];

const defaultDonutData = [
    {
        title: "TBD",
        value: 43,
        colors: ["#0d6efd", "#133b5c"],
        legends: ["TBD", "TBD"]
    },
    {
        title: "TBD",
        value: 43,
        colors: ["#d4af37", "#4b0082"],
        legends: ["TBD", "TBD"]
    },
    {
        title: "TBD",
        value: 43,
        colors: ["#1c8d3f", "#a00000"],
        legends: ["TBD", "TBD"]
    }
];


const FinancialVisualizations = (props) => {

    const {
        geoUrl = defaultGeoUrl,
        usaData = defaultUsaData,
        stateCodes = defaultStateCodes,
        getColor = defaultGetColor,
        legendColors = defaultLegendColors,
        timeSelectionLabels = ["YTD", "QTD", "MTD"],

        barData = defaultBarData,
        lineData = defaultLineData,
        donutData = defaultDonutData,
    } = props;


    /* ----- YOUR UI EXACTLY BELOW THIS LINE ----- */
    return (
        <>
           
           
            <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* LEFT CARD - USA MAP */}
                <Grid item xs={12} md={6}>
                    <Card
                        sx={{
                            borderRadius: "16px",
                            overflow: "hidden",
                            background: "#fff",
                            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
                            height: "100%",
                            minHeight: "480px",
                        }}
                    >
                        <Box
                            sx={{
                                height: "44px",
                                background: "#115f9d",
                                display: "flex",
                                alignItems: "center",
                                color: "#fff",
                                pl: 2,
                                fontSize: "15px",
                                fontWeight: 500,
                            }}
                        >
                            TBD
                        </Box>

                        <Box sx={{ p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <ComposableMap projection="geoAlbersUsa" style={{ width: "100%", height: "320px" }}>
                                <Geographies geography={geoUrl}>
                                    {({ geographies }) =>
                                        geographies.map((geo) => {
                                            const code = stateCodes[geo.id];
                                            const value = usaData[code] || 0;
                                            return (
                                                <Geography
                                                    key={geo.rsmKey}
                                                    geography={geo}
                                                    fill={getColor(value)}
                                                    stroke="#fff"
                                                    strokeWidth={0.6}
                                                    style={{
                                                        default: { outline: "none" },
                                                        hover: { fill: "#1e8f1f", outline: "none" },
                                                        pressed: { outline: "none" }
                                                    }}
                                                />
                                            );
                                        })
                                    }
                                </Geographies>
                            </ComposableMap>

                            <Box sx={{ display: "flex", justifyContent: "center", mt: 3, gap: 3 }}>
                                {legendColors.map((color, index) => (
                                    <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: "50%",
                                                background: color,
                                            }}
                                        />
                                        <Typography sx={{ fontSize: "13px", color: "#555" }}>TBD</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Card>
                </Grid>

                {/* RIGHT CARD - BAR CHART */}
                <Grid item xs={12} md={6}>
                    <Card
                        sx={{
                            borderRadius: "16px",
                            overflow: "hidden",
                            background: "#fff",
                            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
                            height: "100%",
                            minHeight: "480px",
                        }}
                    >
                        <Box
                            sx={{
                                height: "44px",
                                background: "#115f9d",
                                display: "flex",
                                alignItems: "center",
                                color: "#fff",
                                pl: 2,
                                fontSize: "15px",
                                fontWeight: 500,
                            }}
                        >
                            TBD
                        </Box>

                        <Box sx={{ height: "400px", p: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={barData} margin={{ top: 20, right: 20, left: 10, bottom: 60 }}>
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 10 }}
                                        angle={-45}
                                        textAnchor="end"
                                        interval={0}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11 }}
                                        tickFormatter={(v) => `$${v}bn`}
                                    />
                                    <Tooltip />
                                    <Bar dataKey="value">
                                        {barData.map((entry, index) => (
                                            <Cell key={index} fill={entry.color} />
                                        ))}
                                        <LabelList
                                            dataKey="value"
                                            position="top"
                                            formatter={(v) => `$${v}bn`}
                                            style={{ fontSize: "10px" }}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* LINE CHART */}
            <Box sx={{ mt: 8 }}>
                <Card
                    sx={{
                        borderRadius: "18px",
                        background: "#fff",
                        boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                        p: 4,
                        position: "relative",
                        overflow: "visible",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: "-22px",
                            left: "20px",
                            right: "20px",
                            background: "#115f9d",
                            height: "44px",
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            color: "#fff",
                            fontSize: "16px",
                            fontWeight: 500,
                        }}
                    >
                        TBD
                    </Box>

                    <Box sx={{ width: "100%", height: 390, mt: 4 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={lineData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />

                                <XAxis
                                    dataKey="name"
                                    tick={{ fontSize: 12, fill: "#666" }}
                                    interval={0}
                                    height={50}
                                />
                                <YAxis
                                    ticks={[0, 250, 500, 750, 1000, 1250]}
                                    domain={[0, 1250]}
                                    tick={{ fontSize: 12, fill: "#666" }}
                                    axisLine={false}
                                    tickFormatter={(v) => `$${v}`}
                                />
                                <Tooltip formatter={(v) => `$${v}`} />

                                {/* BLUE */}
                                <Line type="monotone" dataKey="a" stroke="#1d4e89" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }}>
                                    <LabelList dataKey="a" position="top" formatter={(v) => `$${v}`} style={{ fill: "#1d4e89", fontSize: 11 }} />
                                </Line>

                                {/* YELLOW */}
                                <Line type="monotone" dataKey="b" stroke="#f4b400" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }}>
                                    <LabelList dataKey="b" position="top" formatter={(v) => `$${v}`} style={{ fill: "#f4b400", fontSize: 11 }} />
                                </Line>

                                {/* GREY */}
                                <Line type="monotone" dataKey="c" stroke="#777" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }}>
                                    <LabelList dataKey="c" position="top" formatter={(v) => `$${v}`} style={{ fill: "#777", fontSize: 11 }} />
                                </Line>

                                <Legend
                                    verticalAlign="bottom"
                                    align="center"
                                    iconType="circle"
                                    iconSize={14}
                                    formatter={() => <span style={{ color: "#555", fontSize: "14px" }}>TBD</span>}
                                    payload={[
                                        { value: "TBD", type: "circle", color: "#1d4e89" },
                                        { value: "TBD", type: "circle", color: "#f4b400" },
                                        { value: "TBD", type: "circle", color: "#777" },
                                    ]}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                </Card>
            </Box>

            {/* DONUT GRID */}
            <Box sx={{ mt: 8 }}>
                <Grid container spacing={3}>
                    {donutData.map((item, idx) => {
                        const chartData = [
                            { name: item.legends[0], value: item.value },
                            { name: item.legends[1], value: 100 - item.value },
                        ];

                        return (
                            <Grid item xs={12} md={4} key={idx}>
                                <Card
                                    sx={{
                                        borderRadius: "18px",
                                        background: "#fff",
                                        boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                                        p: 4,
                                        position: "relative",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: "-22px",
                                            left: "20px",
                                            right: "20px",
                                            background: "#115f9d",
                                            height: "44px",
                                            borderRadius: "10px",
                                            display: "flex",
                                            alignItems: "center",
                                            px: 2,
                                            color: "#fff",
                                            fontSize: "16px",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {item.title}
                                    </Box>

                                    <Box sx={{ width: "100%", height: 260, mt: 4 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={chartData}
                                                    innerRadius="60%"
                                                    outerRadius="90%"
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                    stroke="none"
                                                >
                                                    {item.colors.map((c, i) => (
                                                        <Cell key={i} fill={c} />
                                                    ))}
                                                </Pie>

                                                <Legend
                                                    verticalAlign="bottom"
                                                    align="center"
                                                    content={({ payload }) => (
                                                        <Box sx={{ display: "flex", justifyContent: "center", gap: 4, mt: 1 }}>
                                                            {payload.map((legendItem, index) => (
                                                                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: legendItem.color }} />
                                                                    <Typography sx={{ fontSize: "14px", color: "#555" }}>
                                                                        {legendItem.value}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>

                                    {/* CENTER TEXT */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            textAlign: "center",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "42px", fontWeight: 700 }}>
                                            {item.value}%
                                        </Typography>
                                        <Typography sx={{ fontSize: "16px", color: "#333" }}>
                                            TBD
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>
            <Box sx={{ mt: 8 }}>
                <Grid container spacing={4}>
                    {/* CARD 1 */}
                    <Grid item xs={12} md={6}>
                        <Card
                            sx={{
                                borderRadius: "18px",
                                background: "#fff",
                                boxShadow: "0px 4px 16px rgba(0,0,0,0.15)",
                                p: 4,
                                position: "relative",
                                overflow: "visible",
                                height: 430,
                            }}
                        >
                            {/* Floating Blue Header */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "-22px",
                                    left: "20px",
                                    right: "20px",
                                    height: "44px",
                                    background: "#115f9d",
                                    borderRadius: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    px: 2,
                                    color: "#fff",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                }}
                            >
                                TBD
                            </Box>

                            {/* Center Content */}
                            <Box
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <Typography sx={{ fontSize: "48px", color: "#777" }}>
                                    TBD
                                </Typography>
                            </Box>
                        </Card>
                    </Grid>

                    {/* CARD 2 */}
                    <Grid item xs={12} md={6}>
                        <Card
                            sx={{
                                borderRadius: "18px",
                                background: "#fff",
                                boxShadow: "0px 4px 16px rgba(0,0,0,0.15)",
                                p: 4,
                                position: "relative",
                                overflow: "visible",
                                height: 430,
                            }}
                        >
                            {/* Floating Blue Header */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "-22px",
                                    left: "20px",
                                    right: "20px",
                                    height: "44px",
                                    background: "#115f9d",
                                    borderRadius: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    px: 2,
                                    color: "#fff",
                                    fontSize: "16px",
                                    fontWeight: 500,
                                }}
                            >
                                TBD
                            </Box>
                            {/* Center Content */}
                            <Box
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <Typography sx={{ fontSize: "48px", color: "#777" }}>
                                    TBD
                                </Typography>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default FinancialVisualizations;
